import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { Heading, ExternalLink } from '../';
import { richText } from '../../util/richText';
import { sanitizeUrl } from '../../util/sanitize';

import css from './MarkdownText.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

/**
 * MarkdownText component renders text content with optional markdown support.
 * 
 * Usage examples:
 * 
 * // Basic markdown rendering (default behavior) with contemporary styling
 * <MarkdownText text="# Modern Heading\n\nThis is **bold** text with a [link](http://example.com)\n\n---\n\n- Bullet point 1\n- Bullet point 2\n\n> This is a beautiful blockquote\n\n`inline code` and:\n\n```\ncode block\n```" />
 * 
 * // Disable markdown for plain text
 * <MarkdownText 
 *   text="This is plain text with auto-linkified URLs like http://example.com" 
 *   enableMarkdown={false} 
 * />
 * 
 * // With custom markdown options
 * <MarkdownText 
 *   text="Your markdown content here"
 *   markdownOptions={{
 *     components: {
 *       h1: ({ children }) => <h1 style={{ color: 'red' }}>{children}</h1>
 *     }
 *   }}
 * />
 * 
 * // With heading and custom styling
 * <MarkdownText 
 *   text="Content here..."
 *   heading="Section Title"
 *   showAsIngress={true}
 *   rootClassName="customClass"
 * />
 */

const MarkdownText = ({
  text = null,
  heading = null,
  showAsIngress = false,
  enableMarkdown = true,
  markdownOptions = {},
  rootClassName = null,
  headingLevel = 'h2',
}) => {

  const textClass = showAsIngress ? css.ingress : css.text;
  const sectionClass = rootClassName ? `${css.sectionText} ${rootClassName}` : css.sectionText;

  // Simple table parser for basic markdown tables
  const parseSimpleTables = (content) => {
    if (!content || typeof content !== 'string') return content;

    const lines = content.replace('＞', '>').split('\n');
    let inTable = false;
    let tableRows = [];
    let alignmentRow = null;
    let result = [];
    let currentIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Check if this looks like a table row (contains |)
      if (line.includes('|') && line.split('|').length > 2) {
        if (!inTable) {
          // Add content before table
          if (currentIndex < i) {
            result.push(lines.slice(currentIndex, i).join('\n'));
          }
          inTable = true;
          tableRows = [];
          alignmentRow = null;
          currentIndex = i;
        }

        // Check if this is an alignment/separator row (like |:---|:---:|---:|)
        if (line.match(/^\s*\|[\s\-|:]+\|\s*$/)) {
          alignmentRow = line;
        } else {
          tableRows.push(line);
        }
      } else {
        if (inTable) {
          // End of table, render it
          result.push({ type: 'table', rows: tableRows, alignment: alignmentRow });
          inTable = false;
          currentIndex = i;
        }
      }
    }

    // Handle remaining content
    if (inTable && tableRows.length > 0) {
      result.push({ type: 'table', rows: tableRows, alignment: alignmentRow });
    } else if (currentIndex < lines.length) {
      result.push(lines.slice(currentIndex).join('\n'));
    }

    return result;
  };

  const renderTable = (rows, alignmentRow = null) => {
    if (!rows || rows.length === 0) return null;

    const parseRow = (row) => {
      return row.split('|')
        .slice(1, -1) // Remove empty first/last elements
        .map(cell => cell.trim());
    };

    // Parse alignment information
    const parseAlignment = (alignmentRow) => {
      if (!alignmentRow) return [];

      return alignmentRow.split('|')
        .slice(1, -1) // Remove empty first/last elements
        .map(cell => {
          const trimmed = cell.trim();
          if (trimmed.startsWith(':') && trimmed.endsWith(':')) {
            return 'center';
          } else if (trimmed.endsWith(':')) {
            return 'right';
          } else {
            return 'left';
          }
        });
    };

    const headerRow = parseRow(rows[0]);
    const dataRows = rows.slice(1).map(parseRow);
    const alignments = parseAlignment(alignmentRow);

    return (
      <table className={css.markdownTable} key={Math.random()}>
        <thead className={css.markdownTableHeader}>
          <tr className={css.markdownTableRow}>
            {headerRow.map((cell, index) => (
              <th
                key={index}
                className={css.markdownTableHeaderCell}
                style={{ textAlign: alignments[index] || 'left' }}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={css.markdownTableBody}>
          {dataRows.map((row, rowIndex) => (
            <tr key={rowIndex} className={css.markdownTableRow}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={css.markdownTableCell}
                  style={{ textAlign: alignments[cellIndex] || 'left' }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Custom components for react-markdown
  const markdownComponents = {
    // Override link component to use ExternalLink
    a: ({ href, children, ...props }) => (
      <ExternalLink href={sanitizeUrl(href)} className={css.markdownLink} {...props}>
        {children}
      </ExternalLink>
    ),
    // Override paragraph to use the same styling
    p: ({ children, ...props }) => (
      <p className={textClass} {...props}>
        {children}
      </p>
    ),
    // Style headings with smaller, more contemporary sizes
    h1: ({ children, ...props }) => (
      <h1 className={css.markdownH1} {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 className={css.markdownH2} {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 className={css.markdownH3} {...props}>
        {children}
      </h3>
    ),
    h4: ({ children, ...props }) => (
      <h4 className={css.markdownH4} {...props}>
        {children}
      </h4>
    ),
    h5: ({ children, ...props }) => (
      <h5 className={css.markdownH5} {...props}>
        {children}
      </h5>
    ),
    h6: ({ children, ...props }) => (
      <h6 className={css.markdownH6} {...props}>
        {children}
      </h6>
    ),
    // Style horizontal rules with modern appearance
    hr: ({ ...props }) => (
      <hr className={css.markdownHr} {...props} />
    ),
    // Style unordered lists to show bullet points
    ul: ({ children, ...props }) => (
      <ul className={css.markdownList} {...props}>
        {children}
      </ul>
    ),
    // Style list items with proper bullet display
    li: ({ children, ...props }) => (
      <li className={css.markdownListItem} {...props}>
        {children}
      </li>
    ),
    // Style ordered lists
    ol: ({ children, ...props }) => (
      <ol className={css.markdownOrderedList} {...props}>
        {children}
      </ol>
    ),
    // Style blockquotes
    blockquote: ({ children, ...props }) => (
      <blockquote className={css.markdownBlockquote} {...props}>
        {children}
      </blockquote>
    ),
    // Style code blocks
    code: ({ inline, children, ...props }) => (
      inline ? (
        <code className={css.markdownInlineCode} {...props}>
          {children}
        </code>
      ) : (
        <code className={css.markdownCodeBlock} {...props}>
          {children}
        </code>
      )
    ),
    // Style pre blocks
    pre: ({ children, ...props }) => (
      <pre className={css.markdownPre} {...props}>
        {children}
      </pre>
    ),
    // Handle long words in text nodes
    text: ({ children }) => {
      if (typeof children === 'string' && children.length > MIN_LENGTH_FOR_LONG_WORDS) {
        return <span className={css.longWord}>{children}</span>;
      }
      return children;
    },
    ...markdownOptions.components,
  };

  const parsedContent = enableMarkdown ? parseSimpleTables(text) : text;

  const content = enableMarkdown ? (
    Array.isArray(parsedContent) ? (
      parsedContent.map((item, index) => {
        if (typeof item === 'string') {
          return (
            <ReactMarkdown
              key={index}
              components={markdownComponents}
              {...markdownOptions}
            >
              {item}
            </ReactMarkdown>
          );
        } else if (item.type === 'table') {
          return renderTable(item.rows, item.alignment);
        }
        return null;
      })
    ) : (
      <ReactMarkdown
        components={markdownComponents}
        {...markdownOptions}
      >
        {parsedContent}
      </ReactMarkdown>
    )
  ) : (
    richText(text, {
      linkify: true,
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
      breakChars: '/',
    })
  );

  return text ? (
    <section className={sectionClass}>
      {heading ? (
        <Heading as={headingLevel} rootClassName={css.sectionHeading}>
          {heading}
        </Heading>
      ) : null}
      {enableMarkdown ? (
        content
      ) : (
        <p className={textClass}>{content}</p>
      )}
    </section>
  ) : null;
};

MarkdownText.propTypes = {
  text: PropTypes.string,
  heading: PropTypes.string,
  showAsIngress: PropTypes.bool,
  enableMarkdown: PropTypes.bool,
  markdownOptions: PropTypes.shape({
    components: PropTypes.object,
  }),
  rootClassName: PropTypes.string,
  headingLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
};

export default MarkdownText;
